
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Settings, Save, Trash2, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";

interface ActivitySettingsProps {
  activity: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
}

export const ActivitySettings = ({ activity, open, onOpenChange, onUpdate }: ActivitySettingsProps) => {
  const [loading, setLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: activity?.name || '',
    description: activity?.description || '',
    start_time: activity?.start_time || '',
    end_time: activity?.end_time || '',
    max_capacity: activity?.max_capacity || 20,
    status: activity?.status || 'active',
    days_of_week: activity?.days_of_week || []
  });
  const { toast } = useToast();

  const daysOfWeek = [
    { value: 1, label: 'Lunes' },
    { value: 2, label: 'Martes' },
    { value: 3, label: 'Miércoles' },
    { value: 4, label: 'Jueves' },
    { value: 5, label: 'Viernes' },
    { value: 6, label: 'Sábado' },
    { value: 0, label: 'Domingo' }
  ];

  const handleDayChange = (dayValue: number, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        days_of_week: [...prev.days_of_week, dayValue].sort()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        days_of_week: prev.days_of_week.filter(day => day !== dayValue)
      }));
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      if (!formData.name.trim()) {
        toast({
          title: "Error",
          description: "El nombre de la actividad es obligatorio.",
          variant: "destructive"
        });
        return;
      }

      if (formData.days_of_week.length === 0) {
        toast({
          title: "Error",
          description: "Debe seleccionar al menos un día de la semana.",
          variant: "destructive"
        });
        return;
      }

      const { error } = await supabase
        .from('activities')
        .update({
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          start_time: formData.start_time,
          end_time: formData.end_time,
          max_capacity: formData.max_capacity,
          status: formData.status,
          days_of_week: formData.days_of_week,
          updated_at: new Date().toISOString()
        })
        .eq('id', activity.id);

      if (error) throw error;

      toast({
        title: "Actividad actualizada",
        description: "Los cambios se han guardado exitosamente."
      });

      onUpdate();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron guardar los cambios.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setShowDeleteDialog(false);
      
      // Primero eliminar las inscripciones de estudiantes
      await supabase
        .from('student_activities')
        .delete()
        .eq('activity_id', activity.id);

      // Eliminar registros de asistencia
      await supabase
        .from('attendances')
        .delete()
        .eq('activity_id', activity.id);

      // Finalmente eliminar la actividad
      const { error } = await supabase
        .from('activities')
        .delete()
        .eq('id', activity.id);

      if (error) throw error;

      toast({
        title: "Actividad eliminada",
        description: "La actividad ha sido eliminada exitosamente."
      });

      onUpdate();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar la actividad.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configurar Actividad
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Información básica */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre de la actividad</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej. Fútbol, Teatro, Ajedrez..."
              />
            </div>

            <div>
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción opcional de la actividad..."
                rows={3}
              />
            </div>
          </div>

          {/* Horarios */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_time">Hora de inicio</Label>
              <Input
                id="start_time"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="end_time">Hora de fin</Label>
              <Input
                id="end_time"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
              />
            </div>
          </div>

          {/* Días de la semana */}
          <div>
            <Label>Días de la semana</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {daysOfWeek.map((day) => (
                <div key={day.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`day-${day.value}`}
                    checked={formData.days_of_week.includes(day.value)}
                    onCheckedChange={(checked) => handleDayChange(day.value, checked as boolean)}
                  />
                  <Label htmlFor={`day-${day.value}`} className="text-sm">
                    {day.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Configuración adicional */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="max_capacity">Capacidad máxima</Label>
              <Input
                id="max_capacity"
                type="number"
                min="1"
                value={formData.max_capacity}
                onChange={(e) => setFormData(prev => ({ ...prev, max_capacity: parseInt(e.target.value) || 1 }))}
              />
            </div>
            <div>
              <Label htmlFor="status">Estado</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activa</SelectItem>
                  <SelectItem value="upcoming">Próximamente</SelectItem>
                  <SelectItem value="completed">Finalizada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Información sobre precios */}
          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                  Configuración de Precios
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Los precios de esta actividad se configuran en la sección <strong>Finanzas → Configuración</strong>. 
                  Puedes establecer tanto precios por día como cuotas mensuales para que los padres elijan durante la inscripción.
                </p>
              </div>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex justify-between pt-4">
            <Button
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
              disabled={loading}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar Actividad
            </Button>

            <div className="space-x-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                Guardar Cambios
              </Button>
            </div>
          </div>
        </div>
        
        <DeleteConfirmDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirm={handleDelete}
          title="Eliminar Actividad"
          description="¿Estás seguro de que quieres eliminar esta actividad?"
          itemName={activity?.name}
        />
      </DialogContent>
    </Dialog>
  );
};
